import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import BenchlingConfig from 'BenchlingConfig';
import {isPipelineCapabilityEnabled} from 'pipelines/PipelineCapabilities';
import AssaySchemaErrorList from 'results/assay-schemas/AssaySchemaErrorList';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';

import {safelyGetUserLocale} from '../../../i18n/I18nUtils';
import AutomationFileConfigEditorHeader from '../AutomationFileConfigEditorHeader';
import {
  AutomationFileConfigListEditor,
  BaseAutomationFileConfigListEditorProps,
} from '../AutomationFileConfigListEditor';
import {AutomationOutputFileConfigEditorProps} from '../AutomationFileConfigUtils';
import {RemoveComponentButton} from '../RunSchemaEditorActionButton';
import {
  AutomationOutputFileTableType,
  AutomationOutputFileTableSortType,
  ClientAutomationOutputFileConfigSpec,
  OutputFileAction,
} from '../RunSchemaSpecConstants';
import AutomationOutputFileConfigAnalysisTemplateInfoSection from './AutomationOutputFileConfigAnalysisTemplateInfoSection';
import AutomationOutputFileConfigEditorColumnsMapSection from './AutomationOutputFileConfigEditorColumnsMapSection';
import AutomationOutputFileConfigEditorDatasetSection from './AutomationOutputFileConfigEditorDatasetSection';
import AutomationOutputFileConfigEditorFormatSection from './AutomationOutputFileConfigEditorFormatSection';
import {AutomationOutputFileConfigEditorLocalizationSection} from './AutomationOutputFileConfigEditorLocalizationSection';
import AutomationOutputFileConfigEditorSchemaInfoSection, {
  getDefaultEntitySchemaInfo,
} from './AutomationOutputFileConfigEditorSchemaInfoSection';
import AutomationOutputFileConfigEditorTransformsSection from './AutomationOutputFileConfigEditorTransformsSection';
import {
  AutomationOutputFileConfigTypeSelector,
  AutomationOutputFileConfigType,
} from './AutomationOutputFileConfigTypeSelector';

export function AutomationOutputFileConfigEditor(props: AutomationOutputFileConfigEditorProps): JSX.Element {
  const getInitialConfigType = (): AutomationOutputFileConfigType => {
    if (props.outputFileConfig.analysisTemplate != null && !props.outputFileConfig.shouldCreateDataset) {
      return AutomationOutputFileConfigType.ANALYSIS_TEMPLATE;
    }
    return AutomationOutputFileConfigType.OUTPUT_FILE;
  };

  const [configType, setConfigType] = useState<AutomationOutputFileConfigType>(getInitialConfigType());

  // Only show <AutomationOutputFileConfigTypeSelector> if it is already configured to ANALYSIS_TEMPLATE.
  //
  // This AutomationOutputFileConfig-level analysis template integration is not moving out of beta, in favor
  // of the RunSchema-level integration that supports varying numbers of files.
  //
  // TODO(BNCH-148124): Remove support for this integration entirely.
  const showTypeSelector =
    BenchlingConfig.ENABLE_INSIGHTS_LABS &&
    isAnalysisTemplatesEnabled() &&
    isPipelineCapabilityEnabled('CONNECT') &&
    configType === AutomationOutputFileConfigType.ANALYSIS_TEMPLATE;

  const getSectionNumber = (baseSectionNumber: number): number =>
    showTypeSelector ? baseSectionNumber : baseSectionNumber - 1;

  return (
    <div
      className={css(styles.editorContainer)}
      {...getTestProps({component: 'AutomationOutputFileConfigEditor', element: 'root'})}
    >
      <AssaySchemaErrorList errors={props.errors} component={`Output file configurations ${props.index}`} />
      <AutomationFileConfigEditorHeader
        {...props}
        automationFileConfig={props.outputFileConfig}
        description="This will be the section header for the output file in the run."
      />
      {showTypeSelector && (
        <AutomationOutputFileConfigTypeSelector
          {...props}
          selectedType={configType}
          onTypeChange={setConfigType}
          sectionNumber={2}
        />
      )}
      {configType === AutomationOutputFileConfigType.ANALYSIS_TEMPLATE ? (
        <AutomationOutputFileConfigAnalysisTemplateInfoSection
          {...props}
          sectionNumber={getSectionNumber(3)}
        />
      ) : (
        <>
          <AutomationOutputFileConfigEditorSchemaInfoSection {...props} sectionNumber={getSectionNumber(3)} />
          <AutomationOutputFileConfigEditorTransformsSection {...props} sectionNumber={getSectionNumber(4)} />
          <AutomationOutputFileConfigEditorColumnsMapSection {...props} sectionNumber={getSectionNumber(5)} />
          <AutomationOutputFileConfigEditorFormatSection
            isLastSection={
              !(
                BenchlingConfig.NAV_ENABLE_LOCALE_AWARE_PARSING_FOR_SPREADSHEET_UPLOAD ||
                props.outputFileConfig.shouldCreateDataset
              )
            }
            {...props}
            sectionNumber={getSectionNumber(6)}
          />
          {BenchlingConfig.NAV_ENABLE_LOCALE_AWARE_PARSING_FOR_SPREADSHEET_UPLOAD && (
            <AutomationOutputFileConfigEditorLocalizationSection
              isLastSection={!props.outputFileConfig.shouldCreateDataset}
              {...props}
              sectionNumber={getSectionNumber(7)}
            />
          )}
          {props.outputFileConfig.shouldCreateDataset && (
            <AutomationOutputFileConfigEditorDatasetSection
              {...props}
              sectionNumber={getSectionNumber(
                BenchlingConfig.NAV_ENABLE_LOCALE_AWARE_PARSING_FOR_SPREADSHEET_UPLOAD ? 8 : 7
              )}
            />
          )}
        </>
      )}
      <RemoveComponentButton
        className={css(styles.removeEditorButton)}
        onClick={() => {
          props.onRemoveConfig();
        }}
        dataTestAttrs={{
          component: 'AutomationOutputFileConfigEditor',
          element: 'remove',
          index: `${props.index}`,
        }}
      />
    </div>
  );
}

interface AutomationOutputFileConfigListEditorProps extends BaseAutomationFileConfigListEditorProps {
  automationOutputFileConfigs: Array<ClientAutomationOutputFileConfigSpec>;
  connectionSchemaId: string | null;
  ownerId: string;
}

export function AutomationOutputFileConfigListEditor(
  props: AutomationOutputFileConfigListEditorProps
): JSX.Element {
  const createOutputFileConfig = (): ClientAutomationOutputFileConfigSpec => ({
    name: '',
    delimiter: ',',
    fileExtension: 'csv',
    columnTypes: [],
    tableType: AutomationOutputFileTableType.SAMPLE,
    tableSortType: AutomationOutputFileTableSortType.ALPHABETICAL,
    actionType: OutputFileAction.PURE_REGISTRATION,
    entitySchemaInfo: getDefaultEntitySchemaInfo(),
    shouldCreateDataset: false,
    shouldPerformPlateTransfer: false,
    shouldRecordImages: false,
    shouldAllowAttachment: false,
    spreadsheetProcessingLocale: safelyGetUserLocale(),
  });
  return (
    <AutomationFileConfigListEditor
      {...props}
      automationFileConfigs={props.automationOutputFileConfigs}
      createNewConfig={createOutputFileConfig}
      configEditorClass={AutomationOutputFileConfigEditor}
      getConfigEditorProps={(config, existingConfigs) => ({
        outputFileConfig: config,
        existingOutputFileConfigs: existingConfigs,
        ownerId: props.ownerId,
        connectionSchemaId: props.connectionSchemaId,
      })}
      title="Output file configurations"
      placeholder="Configure how Benchling processes your output files"
      addButtonLabel="Add output file configuration to run"
      dataTestAttrs={{component: 'AutomationOutputFileConfigListEditor', element: 'root'}}
    />
  );
}

const styles = StyleSheet.create({
  editorContainer: {
    position: 'relative',
    width: '100%',
    overflowX: 'hidden',
    paddingBottom: '16px',
    marginBottom: '32px',
    paddingTop: '16px',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '4px',
  },
  editorHeader: {
    padding: '6px',
  },
  removeEditorButton: {
    position: 'absolute',
    zIndex: 30,
    top: '16px',
    right: '16px',
    border: 'none',
    background: 'none',
    float: 'right',
  },
});
